import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calculator, AlertTriangle, TrendingUp, TrendingDown, Info } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import useCpiYoy from '@/hooks/useCpiYoy';
import useEquityBenchmark from '@/hooks/useEquityBenchmark';
import { computeRealYield } from '@/lib/realYield';
import BdcNavGauge from '@/components/BdcNavGauge';
import NiiCoverageStrip from '@/components/NiiCoverageStrip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -----------------------------------------------------------------------------
// DCF (Discounted Earnings) — GuruFocus-style two-stage model.
// -----------------------------------------------------------------------------
// Growth Value    = E₀ · x · (1 − xⁿ) / (1 − x),  x = (1 + g₁) / (1 + d)
// Terminal Value  = E₀ · xⁿ · y · (1 − yᵐ) / (1 − y),  y = (1 + g₂) / (1 + d)
// Intrinsic Value = Growth Value + Terminal Value ( + optional Tangible Book )
//
// Defaults (chosen to match GuruFocus so results are directly comparable):
//   n = 10 growth years, m = 10 terminal years
//   g₂ = sector-aware terminal growth (see SECTOR_TERMINAL_GROWTH below)
//   g₁ = historical 10-yr EPS CAGR (fallback: 5-yr) clamped to [5%, 20%]
//   d  = user's required-return slider (min g₂+1% to keep the math converging)
//   E₀ = TTM EPS w/o NRI (primary) or FCF/share (secondary, when
//        earningsMode is 'fcf' OR EPS ≤ 0)
// -----------------------------------------------------------------------------
const DCF_N_GROWTH_YEARS = 10;
const DCF_M_TERMINAL_YEARS = 10;
const DCF_G_FLOOR = 0.05;
const DCF_G_CAP = 0.20;

// Sep 2026 — Sector-aware terminal growth (g₂).
// The default across all sectors was 4% (matching GuruFocus).
// Reality: utilities and real estate structurally grow at inflation
// or below, while tech grows above inflation for decades on end.
// A single 4% terminal number over-values utilities and under-values
// tech in the terminal stage. Sector-specific defaults line the
// terminal rate up with each sector's long-run economic ceiling.
// Values are chosen to be defensible in a beginner-facing product —
// none exceeds long-run US nominal GDP growth (~5%).
const SECTOR_TERMINAL_GROWTH = {
  'Utilities': 0.02,
  'Real Estate': 0.03,
  'Energy': 0.03,
  'Consumer Staples': 0.035,
  'Financial Services': 0.035,
  'Financials': 0.035,
  'Basic Materials': 0.035,
  'Materials': 0.035,
  'Industrials': 0.04,
  'Healthcare': 0.04,
  'Consumer Cyclical': 0.04,
  'Consumer Discretionary': 0.04,
  'Communication Services': 0.045,
  'Technology': 0.05,
  'Information Technology': 0.05,
};
const DCF_TERMINAL_GROWTH_DEFAULT = 0.04;

function getTerminalGrowth(sector) {
  if (!sector) return { g2: DCF_TERMINAL_GROWTH_DEFAULT, isDefault: true, sector: null };
  const key = String(sector).trim();
  if (SECTOR_TERMINAL_GROWTH[key] != null) {
    return { g2: SECTOR_TERMINAL_GROWTH[key], isDefault: false, sector: key };
  }
  return { g2: DCF_TERMINAL_GROWTH_DEFAULT, isDefault: true, sector: key };
}

function computeDCFValue({ E0, growthCagrPct, r, terminalGrowth, earningsLabel, tangibleBookPerShare, addTangibleBook }) {
  if (!(E0 > 0)) {
    return { ok: false, reason: `${earningsLabel || 'Earnings'} is zero or negative — DCF math is undefined.` };
  }
  if (growthCagrPct == null) {
    return { ok: false, reason: `Not enough ${(earningsLabel || 'earnings').toLowerCase()} history to compute a growth rate.` };
  }
  // Clamp growth per GuruFocus rule to keep the model sane.
  const g1 = Math.max(DCF_G_FLOOR, Math.min(DCF_G_CAP, growthCagrPct / 100));
  let gClamped = null;
  if (growthCagrPct / 100 > DCF_G_CAP) gClamped = 'high';
  else if (growthCagrPct / 100 < DCF_G_FLOOR) gClamped = 'low';

  const g2 = terminalGrowth != null ? terminalGrowth : DCF_TERMINAL_GROWTH_DEFAULT;
  // Discount rate must exceed terminal growth or the sum diverges.
  const d = Math.max(r, g2 + 0.01);

  const x = (1 + g1) / (1 + d);
  const y = (1 + g2) / (1 + d);

  // Growth stage PV. x is virtually never exactly 1 for real inputs
  // but guard against a divide-by-zero anyway.
  const growthPV = Math.abs(1 - x) < 1e-9
    ? E0 * DCF_N_GROWTH_YEARS
    : E0 * x * (1 - Math.pow(x, DCF_N_GROWTH_YEARS)) / (1 - x);

  const terminalPV = Math.abs(1 - y) < 1e-9
    ? E0 * Math.pow(x, DCF_N_GROWTH_YEARS) * DCF_M_TERMINAL_YEARS
    : E0 * Math.pow(x, DCF_N_GROWTH_YEARS) * y * (1 - Math.pow(y, DCF_M_TERMINAL_YEARS)) / (1 - y);

  // Sep 2026 — Optional tangible-book add-on (GuruFocus feature).
  // Adds (equity − goodwill − intangibles) / shares to the intrinsic
  // value. This gives asset-heavy names (REITs, banks, industrials)
  // credit for their liquidation floor. Negative TBV is added as-is
  // so an over-leveraged balance sheet correctly LOWERS the FV,
  // matching GuruFocus's convention.
  const bookAdd = (addTangibleBook && tangibleBookPerShare != null) ? tangibleBookPerShare : 0;
  const value = growthPV + terminalPV + bookAdd;
  if (!Number.isFinite(value) || value <= 0) {
    return { ok: false, reason: 'DCF produced a non-positive result — inputs are outside the model\'s useful range.' };
  }
  return {
    ok: true,
    value,
    growthPV,
    terminalPV,
    bookAdd,
    tangibleBookPerShare: tangibleBookPerShare ?? null,
    g1Used: g1,
    g2Used: g2,
    dUsed: d,
    gClamped,
  };
}

/**
 * ValuationMatrixCard — Round 95c (Jun 2026).
 *
 * Single-stock intrinsic-value engine for the Insights tab. Routes the
 * DDM (Dividend Discount Model) variant by asset class — running the
 * wrong variant on the wrong asset produces garbage prices, so we
 * detect and switch explicitly:
 *
 *   • Preferred Share / Baby Bond → Zero-Growth Perpetuity
 *           P = D / r
 *     (fixed coupon, no growth, dividend amount AND par are known.)
 *
 *   • REIT (Equity)               → Gordon Constant Growth
 *           P = D₀ × (1 + g) / (r − g)
 *     (Use CAGR as g; we already strip specials upstream so the
 *     5-year CAGR is honest. Backend Round 95c.)
 *
 *   • BDC                         → DDM bypassed. Display reasoning;
 *     point user to NII coverage + NAV spread (future Phase 5 card).
 *
 *   • CEF                         → DDM bypassed. Display reasoning;
 *     point user to Premium/Discount gauge (future Phase 5 card).
 *
 *   • Regular Common Stock (incl. ADR):
 *       - 5-yr clean CAGR ≤ 6% → Gordon Constant Growth
 *       - 5-yr clean CAGR > 6% → Two-Stage DDM
 *           Stage 1: high-growth (g₁ = CAGR) for n years
 *           Stage 2: terminal (g₂ = 3.5% — long-run inflation+GDP floor)
 *           n = 5 if g ≤ 12%, 3 if g ≤ 20%, 2 otherwise
 *
 * Guardrails:
 *   • Cap g₁ at 20% (extreme growth flameout protection).
 *   • If r ≤ g, clamp r = g + 0.01 so the math doesn't divide by zero.
 *   • If we can't compute (no dividend, no growth data), surface the
 *     reason instead of a number.
 *
 * Inputs come straight from `stock` + `/dividend-growth`:
 *   • D₀ = stock.dividend_rate (forward annual)
 *   • g  = dividend-growth.cagr_5yr / 100 (Massive specials-filtered)
 *   • r  = local slider state, default 8%, range 6-15%
 */

function computeIntrinsicValue({ assetClass, D0, g, r, couponDividend, parValue }) {
  // Preferred / Baby Bond — fixed coupon, no growth, dividend known.
  if (assetClass === 'preferred_share' || assetClass === 'baby_bond') {
    const D = couponDividend != null ? couponDividend : D0;
    if (!(D > 0) || !(r > 0)) return { ok: false, reason: 'Missing coupon or required-return inputs.' };
    return {
      ok: true,
      model: 'Zero-Growth Perpetuity',
      value: D / r,
      formula: `P = $${D.toFixed(4)} / ${(r * 100).toFixed(1)}%`,
      assumptions: parValue ? [`Par value: $${Number(parValue).toFixed(2)}`, `Annual coupon: $${D.toFixed(4)}`, `Required return: ${(r * 100).toFixed(1)}%`] : [],
    };
  }

  // BDC / CEF — DDM is the wrong tool. Surface that explicitly.
  if (assetClass === 'bdc') {
    return { ok: false, model: 'BDC — DDM Bypassed', reason: 'BDC distributions blend recurring NII with one-off realized gains, so a CAGR-driven DDM misfires. The gauges below show what BDC investors actually watch: Price vs NAV and NII coverage.' };
  }
  if (assetClass === 'cef') {
    return { ok: false, model: 'CEF — DDM Bypassed', reason: "CEF distributions can include return of capital, realized gains, and option premium. There's no \"earnings retention\" behind them, so DDM math breaks. Use the 1-Year Z-Score strip and Premium/Discount-to-NAV above instead." };
  }

  if (!(D0 > 0)) return { ok: false, reason: "We don't have a forward dividend rate for this ticker yet." };
  if (g == null) return { ok: false, reason: "Not enough dividend history to compute a clean 5-year growth rate." };

  // Cap g at 20% — extreme high-growth phases burn out fast and
  // a 25%+ CAGR plugged straight into DDM produces sci-fi numbers.
  let gEff = Math.min(Math.max(g, 0), 0.20);
  // Round 95c v2 (Jun 2026) — Slider-respecting guardrail.
  // If the user's required return is lower than the company's
  // historical growth, the original formulas blow up. Old code:
  //   `rEff = max(r, gEff + 0.01)` — silently bumped r ABOVE the
  // slider value when CAGR was high (COST 17.6% CAGR meant the
  // slider was effectively dead from 6% to 16.5%). New behavior
  // matches the AI's recommendation: clamp growth DOWN to (r - 1%)
  // so the slider always changes the output, and surface a tag so
  // the user knows their requirement is squeezing the model.
  const rEff = Math.max(r, 0.02);
  let gClamped = false;
  if (gEff >= rEff) {
    gClamped = true;
    gEff = Math.max(0, rEff - 0.01);
  }

  // ≤6% CAGR → Gordon Constant Growth. Also routes when the company
  // pays a flat or declining dividend (g ≤ 0): pretend g = 0 and use
  // the perpetuity simplification so the price is at least defined.
  if (gEff <= 0.06) {
    const gUsed = Math.max(gEff, 0);
    const denom = rEff - gUsed;
    if (denom <= 0) return { ok: false, reason: 'Required return must exceed growth rate.' };
    const P = (D0 * (1 + gUsed)) / denom;
    const assumptions = [
      `Forward dividend (D₀): $${D0.toFixed(4)}`,
      `Stable growth (g): ${(gUsed * 100).toFixed(2)}% ${gClamped ? '(clamped DOWN — your required return is below the 5-yr CAGR)' : '(5-yr clean CAGR)'}`,
      `Required return (r): ${(rEff * 100).toFixed(1)}%`,
    ];
    return {
      ok: true,
      model: 'Gordon Constant Growth',
      value: P,
      formula: `P = $${D0.toFixed(4)} × (1 + ${(gUsed * 100).toFixed(1)}%) / (${(rEff * 100).toFixed(1)}% − ${(gUsed * 100).toFixed(1)}%)`,
      assumptions,
      gClamped,
    };
  }

  // High-growth → Two-Stage DDM. Choose n based on how aggressive
  // the growth is — the faster, the shorter the runway.
  const n = gEff <= 0.12 ? 5 : (gEff <= 0.20 ? 3 : 2);
  const g2 = 0.035; // long-run inflation + GDP floor
  if (rEff <= g2) return { ok: false, reason: 'Required return must exceed terminal growth rate (3.5%).' };

  let stage1PV = 0;
  let dt = D0;
  for (let t = 1; t <= n; t++) {
    dt = dt * (1 + gEff);
    stage1PV += dt / Math.pow(1 + rEff, t);
  }
  const matureDividend = dt * (1 + g2);
  const terminalPrice = matureDividend / (rEff - g2);
  const terminalPV = terminalPrice / Math.pow(1 + rEff, n);
  const P = stage1PV + terminalPV;

  return {
    ok: true,
    model: `Two-Stage DDM (${n}-yr high growth)`,
    value: P,
    formula: `Stage 1 (${n} yrs @ ${(gEff * 100).toFixed(1)}%) + Terminal (${(g2 * 100).toFixed(1)}%)`,
    assumptions: [
      `Forward dividend (D₀): $${D0.toFixed(4)}`,
      `Stage 1 growth (g₁): ${(gEff * 100).toFixed(2)}% for ${n} years ${gClamped ? '(clamped DOWN — your required return is below the 5-yr CAGR)' : ''}`.trim(),
      `Terminal growth (g₂): ${(g2 * 100).toFixed(1)}% forever`,
      `Required return (r): ${(rEff * 100).toFixed(1)}%`,
    ],
    gClamped,
  };
}

export default function ValuationMatrixCard({ stock, livePrice }) {
  const [growth, setGrowth] = useState(null);
  const [dcfInputs, setDcfInputs] = useState(null);
  // Round 95c — per-modal slider that resets to 8% each open (user
  // explicitly chose this over a per-user setting).
  // Internal state ALWAYS stores the NOMINAL required return. The
  // "Real / Nominal" display toggle below only changes labels;
  // the DDM math is invariant either way (a real g and a real r
  // produce the same intrinsic value as their nominal counterparts).
  const [requiredReturn, setRequiredReturn] = useState(0.08);
  const [realDiscountMode, setRealDiscountMode] = useState(false);
  // Sep 2026 — Valuation Matrix DCF blend:
  //   • viewMode: 'blend' (default, payout-weighted DDM+DCF), 'ddm', 'dcf'
  //   • earningsMode: 'eps' (default, GuruFocus standard) or 'fcf'
  //   • addTangibleBook: optional +TBV toggle (GuruFocus liquidation-floor
  //     feature). Auto-suggested ON for asset-heavy sectors (Real Estate,
  //     Financials) where liquidation value materially anchors the FV.
  const [viewMode, setViewMode] = useState('blend');
  const [earningsMode, setEarningsMode] = useState('eps');
  const [addTangibleBook, setAddTangibleBook] = useState(false);
  // Sep 2026 — Growth-stage override. `null` means "use the trailing
  // 10-yr CAGR from the data feed (clamped 5-20%)". Any number is a
  // user-supplied growth rate in decimal form (0.05 = 5%) that
  // completely replaces the trailing CAGR — useful when analyst
  // forward estimates or a known business inflection deserve to
  // beat what the past ten years say.
  const [growthOverride, setGrowthOverride] = useState(null);
  // Hidden diagnostic view — reveals the full sector → g₂ map so
  // power-users can audit which terminal rate applies to each
  // sector without spelunking through code.
  const [showSectorMap, setShowSectorMap] = useState(false);
  const cpiYoy = useCpiYoy();
  // Round 101 (Jul 2026, revised 101b) — Live Damodaran benchmark
  // via the shared singleton hook so this card + the Portfolio
  // Contrast card ERP chip share ONE cached network call.
  const equityBench = useEquityBenchmark();
  const equityBenchmark = {
    pct: equityBench?.historical_real_equity_pct ?? 6.7,
    period: equityBench?.period ?? '1928\u20132023',
    is_default: !!equityBench?.is_default,
  };

  useEffect(() => {
    if (!stock?.ticker) return undefined;
    let cancelled = false;
    setGrowth(null);
    setDcfInputs(null);
    setRequiredReturn(0.08); // reset per-modal default
    setViewMode('blend');
    setEarningsMode('eps');
    // Auto-suggest tangible-book add-on for asset-heavy sectors where
    // liquidation value materially anchors the fair value (REITs,
    // banks, insurance, industrials). User can turn it off with the
    // "+ Book" pill.
    const sec = String(stock?.sector_norm || stock?.sector || '').trim();
    const bookHeavy = ['Real Estate', 'Financial Services', 'Financials', 'Industrials'].includes(sec);
    setAddTangibleBook(bookHeavy);
    setGrowthOverride(null);
    setShowSectorMap(false);
    axios.get(`${API}/stocks/${stock.ticker}/dividend-growth`)
      .then((r) => { if (!cancelled) setGrowth(r.data || null); })
      .catch(() => { if (!cancelled) setGrowth(null); });
    axios.get(`${API}/stocks/${stock.ticker}/dcf-inputs`)
      .then((r) => { if (!cancelled) setDcfInputs(r.data || null); })
      .catch(() => { if (!cancelled) setDcfInputs(null); });
    return () => { cancelled = true; };
  }, [stock?.ticker]);

  // Detect asset class. `assetClass` mirrors backend's `asset_class`
  // string; we also fall back to the boolean flags the stock object
  // already carries for CEF / BDC / REIT / ADR.
  const assetClass = useMemo(() => {
    if (stock?.asset_class === 'preferred_share' || stock?.asset_class === 'baby_bond') return stock.asset_class;
    if (stock?.is_cef) return 'cef';
    if (stock?.is_bdc) return 'bdc';
    if (stock?.is_reit) return 'reit';
    if (stock?.is_adr) return 'adr';
    return 'common';
  }, [stock?.asset_class, stock?.is_cef, stock?.is_bdc, stock?.is_reit, stock?.is_adr]);

  const D0 = Number(stock?.dividend_rate || 0);
  // Backend now ships a specials-filtered CAGR. Convert to fractional
  // rate (server returns a percent).
  const g = growth?.cagr_5yr != null ? Number(growth.cagr_5yr) / 100 : null;

  // Preferred / Baby Bond: use the coupon × par directly, since the
  // `dividend_rate` field is already coupon × par for these assets.
  const couponDividend = stock?.coupon_rate && stock?.par_value
    ? Number(stock.coupon_rate) * Number(stock.par_value)
    : D0;

  const result = useMemo(() => computeIntrinsicValue({
    assetClass,
    D0,
    g,
    r: requiredReturn,
    couponDividend,
    parValue: stock?.par_value,
  }), [assetClass, D0, g, requiredReturn, couponDividend, stock?.par_value]);

  // Sep 2026 — DCF variant + payout-weighted blend.
  // - Not computed for preferreds / baby bonds (perpetuity is already
  //   the correct model and there's no earnings retention story), or
  //   for BDC / CEF (DDM bypass path — see reason strings above).
  // - EPS is the default earnings base (GuruFocus's finding is that
  //   EPS correlates better with long-run price than FCF does); we
  //   auto-switch to FCF/share when EPS ≤ 0 so loss-making but
  //   cash-positive names still get a valuation.
  const dcf = useMemo(() => {
    if (!dcfInputs) return { ok: false, reason: 'DCF inputs still loading.' };
    if (['preferred_share', 'baby_bond', 'bdc', 'cef'].includes(assetClass)) {
      return { ok: false, skip: true };
    }
    const useFcf = earningsMode === 'fcf' || !(dcfInputs.eps_ttm > 0);
    const E0 = useFcf ? dcfInputs.fcf_per_share_ttm : dcfInputs.eps_ttm;
    const trailingCagr = useFcf
      ? (dcfInputs.fcf_cagr_10y_pct ?? dcfInputs.fcf_cagr_5y_pct)
      : (dcfInputs.eps_cagr_10y_pct ?? dcfInputs.eps_cagr_5y_pct);
    const yrsAvail = useFcf ? dcfInputs.fcf_years_available : dcfInputs.eps_years_available;
    const historyWindow = (useFcf ? dcfInputs.fcf_cagr_10y_pct : dcfInputs.eps_cagr_10y_pct) != null ? '10-yr' : '5-yr';
    // User override wins over the trailing CAGR; passed to compute as
    // a percent so the [5%, 20%] clamp still applies (guards against
    // an accidental 90% growth rate).
    const cagr = growthOverride != null ? growthOverride * 100 : trailingCagr;
    const term = getTerminalGrowth(stock?.sector_norm || stock?.sector);
    const out = computeDCFValue({
      E0,
      growthCagrPct: cagr,
      r: requiredReturn,
      terminalGrowth: term.g2,
      earningsLabel: useFcf ? 'FCF/share' : 'EPS',
      tangibleBookPerShare: dcfInputs.tangible_book_per_share,
      addTangibleBook,
    });
    return {
      ...out,
      base: useFcf ? 'fcf' : 'eps',
      E0,
      trailingCagrPct: trailingCagr,
      cagrPct: cagr,
      historyWindow,
      yrsAvail,
      isOverridden: growthOverride != null,
      terminalSector: term.sector,
      terminalIsDefault: term.isDefault,
    };
  }, [dcfInputs, assetClass, earningsMode, requiredReturn, stock?.sector_norm, stock?.sector, addTangibleBook, growthOverride]);

  // Payout-weighted blended intrinsic value.
  // weight_DDM = payout ratio  (fraction of earnings returned as div)
  // weight_DCF = 1 − payout    (fraction retained for growth/buybacks)
  //
  // Payout-ratio source order:
  //   1. Backend-provided value (Massive `payout_ratio` or |div| / EPS)
  //   2. Frontend compute: D₀ / eps_ttm (when both are on hand)
  //   3. Fallback: 0.7 DDM / 0.3 DCF — deliberately tilted toward DDM
  //      when we truly have no signal, since this card only renders
  //      for dividend-paying names.
  //
  // If DCF is unavailable (no EPS/FCF history), the blend collapses
  // to pure DDM (`ddmOnly: true`) which is EXACTLY the old pre-blend
  // behaviour — nothing regresses.
  const blend = useMemo(() => {
    const ddmOk = result.ok;
    const dcfOk = dcf.ok;
    // Source of truth cascade for payout.
    let payoutSource = 'backend';
    let payout = dcfInputs?.payout_ratio;
    if (payout == null || payout < 0) {
      // Try to compute from D₀ / eps_ttm (both usually on hand).
      if (D0 > 0 && dcfInputs?.eps_ttm > 0) {
        payout = Math.min(1, D0 / dcfInputs.eps_ttm);
        payoutSource = 'computed';
      } else {
        payout = 0.7; // DDM-heavy safe default
        payoutSource = 'fallback';
      }
    }
    payout = Math.min(1, Math.max(0, payout));
    if (ddmOk && dcfOk) {
      return {
        ok: true,
        value: payout * result.value + (1 - payout) * dcf.value,
        payout,
        payoutSource,
      };
    }
    if (ddmOk) return { ok: true, value: result.value, payout: 1, ddmOnly: true, payoutSource };
    if (dcfOk) return { ok: true, value: dcf.value, payout: 0, dcfOnly: true, payoutSource };
    return { ok: false };
  }, [result, dcf, dcfInputs, D0]);

  // Which value to render as the headline "Target Buy".
  const displayValue = useMemo(() => {
    if (viewMode === 'ddm') return result.ok ? result.value : null;
    if (viewMode === 'dcf') return dcf.ok ? dcf.value : null;
    return blend.ok ? blend.value : null;
  }, [viewMode, result, dcf, blend]);

  // Round 95c v2 — live price now arrives explicitly via prop from
  // StockModal (`displayPrice` = streamed Massive last trade ?? cached
  // snapshot price). The old `stock.regular_market_price` /
  // `stock.current_price` lookups never resolved because the stock
  // object uses `price` and the WebSocket layer hands the live tick
  // through a separate state.
  const priceFromStock = Number(stock?.price || stock?.regular_market_price || stock?.current_price || 0);
  const effectiveLive = Number(livePrice || 0) || priceFromStock;
  const delta = displayValue != null && effectiveLive > 0 ? (displayValue - effectiveLive) / effectiveLive : null;
  const underValued = delta != null && delta > 0.05;
  const overValued = delta != null && delta < -0.05;

  // Hide entirely if we have nothing useful to show for a brand-new
  // ticker with no price + no dividend + no class signal.
  if (!stock?.ticker) return null;
  if (assetClass === 'common' && !D0) return null;

  return (
    <div className="rounded-lg border bg-card p-3" data-testid="valuation-matrix-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <Calculator className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
            <AbbrTip term="INTRINSIC_VALUE">Valuation Matrix — Target Buy Price</AbbrTip>
          </h4>
        </div>
        {/* Model badge — wraps in the matching AbbrTip so a tap on the
            chip pops the Gordon / Two-Stage / Zero-Growth definition. */}
        <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
          {(() => {
            const m = result.model || '';
            if (m.startsWith('Gordon')) return <AbbrTip term="GORDON_DDM" className="!text-muted-foreground">{m}</AbbrTip>;
            if (m.startsWith('Two-Stage')) return <AbbrTip term="TWO_STAGE_DDM" className="!text-muted-foreground">{m}</AbbrTip>;
            if (m.startsWith('Zero-Growth')) return <AbbrTip term="ZERO_GROWTH_PERPETUITY" className="!text-muted-foreground">{m}</AbbrTip>;
            return m || '—';
          })()}
        </span>
      </div>

      {/* Required-rate-of-return slider — per-modal, resets to 8%. */}
      {(assetClass === 'common' || assetClass === 'adr' || assetClass === 'reit' || assetClass === 'preferred_share' || assetClass === 'baby_bond') && (
        <div className="mb-3 px-1" data-testid="valuation-required-return-slider-wrap">
          <div className="flex items-center justify-between text-[11px] font-bold text-foreground mb-1 gap-2 flex-wrap">
            <span>
              Your <AbbrTip term="REQUIRED_RETURN">required rate of return</AbbrTip>
            </span>
            <div className="flex items-center gap-1.5">
              {/* Jul 7, 2026 — Nominal / Real display toggle. Slider
                  value stays nominal internally (DDM invariant to
                  frame) but the label + tick marks flip to show the
                  Fisher-real equivalent so users who think in real-
                  return terms can set their target that way. */}
              {typeof cpiYoy === 'number' && (
                <div
                  className="inline-flex text-[9px] font-bold rounded-full border border-border overflow-hidden"
                  data-testid="valuation-real-mode-toggle"
                >
                  <button
                    type="button"
                    onClick={() => setRealDiscountMode(false)}
                    className={`px-1.5 py-0.5 transition-colors ${!realDiscountMode ? 'bg-fuchsia-500 text-white' : 'text-muted-foreground hover:bg-muted/40'}`}
                    title="Show slider values as nominal rates (uncorrected for inflation)."
                    data-testid="valuation-real-mode-nominal"
                  >
                    NOM
                  </button>
                  <button
                    type="button"
                    onClick={() => setRealDiscountMode(true)}
                    className={`px-1.5 py-0.5 transition-colors ${realDiscountMode ? 'bg-fuchsia-500 text-white' : 'text-muted-foreground hover:bg-muted/40'}`}
                    title={`Show slider values as REAL rates — the Fisher-exact inflation-adjusted equivalent at current CPI ${cpiYoy.toFixed(2)}%. Fair-value output unchanged; only the label frame flips.`}
                    data-testid="valuation-real-mode-real"
                  >
                    REAL
                  </button>
                </div>
              )}
              <span className="font-mono text-foreground">
                {realDiscountMode && typeof cpiYoy === 'number'
                  ? `${(computeRealYield(requiredReturn * 100, cpiYoy) ?? 0).toFixed(2)}% real`
                  : `${(requiredReturn * 100).toFixed(1)}%`}
              </span>
            </div>
          </div>
          {/* Round 96j (Feb 2026) — Touch-shield wrapper. The StockModal's
              tab strip listens for left/right swipes on the outer
              container so phone users can swipe between tabs. The
              gesture was ALSO swallowing finger drags on the slider:
              every time the user finished sliding the value bar, the
              touch-end bubbled up and triggered a tab change. We stop
              the touch-event propagation here so the parent swipe
              handler never sees the drag. The 6px (`py-1.5`) vertical
              padding makes the shield a few millimetres taller than
              the bar itself so a slightly-imprecise finger tap that
              lands a hair above or below the rail also stays inside
              the shield instead of leaking to the tab swipe. */}
          <div
            className="py-1.5"
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            data-testid="valuation-slider-touch-shield"
          >
            <input
              type="range"
              min={6}
              max={15}
              step={0.5}
              value={requiredReturn * 100}
              onChange={(e) => setRequiredReturn(Number(e.target.value) / 100)}
              className="w-full accent-fuchsia-500"
              data-testid="valuation-required-return-slider"
              aria-label="Required rate of return"
            />
          </div>
          <div className="flex justify-between text-[10px] font-bold text-muted-foreground mt-0.5">
            {realDiscountMode && typeof cpiYoy === 'number' ? (
              <>
                <span>{(computeRealYield(6, cpiYoy) ?? 0).toFixed(1)}% real</span>
                <span>{(computeRealYield(10, cpiYoy) ?? 0).toFixed(1)}% real</span>
                <span>{(computeRealYield(15, cpiYoy) ?? 0).toFixed(1)}% real</span>
              </>
            ) : (
              <>
                <span>6% conservative</span>
                <span>10% market avg</span>
                <span>15% aggressive</span>
              </>
            )}
          </div>
          {realDiscountMode && typeof cpiYoy === 'number' && (
            <div
              className="mt-1 text-[10px] text-muted-foreground italic text-center"
              data-testid="valuation-real-mode-note"
            >
              Nominal equivalent: {(requiredReturn * 100).toFixed(1)}% @ CPI {cpiYoy.toFixed(2)}%. Fair-value output is identical either way — the DDM already returns today&apos;s dollars.
            </div>
          )}
          {/* Jul 7, 2026 — Historical benchmark chip. Shows the
              long-run US equity REAL return (Damodaran 1928–2023 =
              6.7%) alongside the user's current REAL required
              return so they can tell at a glance whether their bar
              is soft/balanced/aggressive. Renders on its own row
              so mobile doesn't have to squeeze it inline with the
              slider value. Sits below the NOM/REAL footer note. */}
          {typeof cpiYoy === 'number' && (() => {
            const HISTORICAL_REAL_EQUITY_PCT = equityBenchmark.pct;
            const userReal = computeRealYield(requiredReturn * 100, cpiYoy);
            if (userReal == null) return null;
            const delta = userReal - HISTORICAL_REAL_EQUITY_PCT;
            let tone = 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 ring-emerald-300/40';
            let label = 'balanced';
            if (userReal < HISTORICAL_REAL_EQUITY_PCT - 1.5) {
              tone = 'text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 ring-amber-300/50';
              label = 'soft — margin of safety thin';
            } else if (userReal > HISTORICAL_REAL_EQUITY_PCT + 1.5) {
              tone = 'text-rose-800 dark:text-rose-200 bg-rose-50 dark:bg-rose-950/40 ring-rose-300/50';
              label = 'aggressive — few stocks will pass';
            }
            const currentRealRf = equityBench?.current_real_tbond_pct;
            const requiredRiskPremium = typeof currentRealRf === 'number' ? userReal - currentRealRf : null;
            return (
              <div className="mt-1.5 w-full space-y-1" data-testid="valuation-historical-benchmark">
                <div
                  className={`inline-flex flex-wrap items-center justify-center gap-1 px-2 py-1 rounded-md ring-1 text-[10px] font-semibold w-full ${tone}`}
                  title={`Long-run US equity REAL return averaged ${HISTORICAL_REAL_EQUITY_PCT.toFixed(2)}% per year over ${equityBenchmark.period} (Damodaran NYU Stern dataset${equityBenchmark.is_default ? ', last-known default' : ', live'}). Your current required real return of ${userReal.toFixed(2)}% is ${Math.abs(delta).toFixed(2)}pp ${delta >= 0 ? 'ABOVE' : 'BELOW'} that historical baseline.`}
                >
                  <span className="font-mono">You: {userReal.toFixed(1)}% real</span>
                  <span className="text-muted-foreground/70 font-normal">·</span>
                  <span className="font-mono">
                    <AbbrTip term="DAMODARAN_ERP" className="!font-mono">history: {HISTORICAL_REAL_EQUITY_PCT.toFixed(1)}%</AbbrTip>
                  </span>
                  <span className="text-muted-foreground/70 font-normal">·</span>
                  <span>{label}</span>
                </div>
                {/* Round 101d (Jul 7, 2026) — Current-frame companion.
                    Shows the implied risk premium the user is asking
                    for over TODAY's actual risk-free real rate. Users
                    can immediately see whether their DCF discount rate
                    implies a reasonable premium given current bond
                    yields — a much more actionable question than
                    "vs. 98-year average" for a today-facing valuation. */}
                {typeof requiredRiskPremium === 'number' && (() => {
                  // Round 101e — One-tap recommender. Target a
                  // Damodaran-baseline 4.5pp equity risk premium over
                  // today's risk-free real rate. Reverse-Fisher gives
                  // the nominal required return that would produce
                  // that premium, snapped to the slider's 0.5% step
                  // and clamped to the [6%, 15%] slider range.
                  const TARGET_ERP_PP = 4.5;
                  const targetReal = currentRealRf + TARGET_ERP_PP;
                  const targetNominal = ((1 + targetReal / 100) * (1 + cpiYoy / 100) - 1) * 100;
                  const snappedNominal = Math.min(15, Math.max(6, Math.round(targetNominal * 2) / 2));
                  const currentSlider = Math.round(requiredReturn * 200) / 2; // current in 0.5% steps
                  const showRecommend = Math.abs(snappedNominal - currentSlider) >= 0.5;
                  return (
                    <div
                      className="inline-flex flex-wrap items-center justify-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] text-muted-foreground w-full ring-1 ring-border/40"
                      title={`Today's risk-free real rate: ${currentRealRf.toFixed(2)}% (10Y Treasury nominal Fisher-adjusted against current CPI YoY). Your required real return of ${userReal.toFixed(2)}% implies a ${Math.abs(requiredRiskPremium).toFixed(2)}pp risk premium over today's safe alternative. Historical equity risk premium has averaged ~${TARGET_ERP_PP.toFixed(1)}pp (Damodaran); values much below suggest the DCF is too generous, values much above will filter out most quality names.`}
                      data-testid="valuation-current-frame-benchmark"
                    >
                      <span className="font-mono">today&apos;s risk-free: {currentRealRf.toFixed(1)}% real</span>
                      <span className="text-muted-foreground/50 font-normal">·</span>
                      <span className={`font-mono font-semibold ${requiredRiskPremium >= 4.5 ? 'text-emerald-700 dark:text-emerald-300' : requiredRiskPremium >= 2 ? 'text-amber-700 dark:text-amber-300' : 'text-rose-700 dark:text-rose-300'}`}>
                        your risk premium: {requiredRiskPremium >= 0 ? '+' : ''}{requiredRiskPremium.toFixed(1)}pp
                      </span>
                      {showRecommend && (
                        <>
                          <span className="text-muted-foreground/50 font-normal">·</span>
                          <button
                            type="button"
                            onClick={() => setRequiredReturn(snappedNominal / 100)}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold font-mono transition-colors cursor-pointer"
                            title={`Snap the required-return slider to ${snappedNominal.toFixed(1)}% (nominal) — the value that lands your risk premium at the ${TARGET_ERP_PP.toFixed(1)}pp long-run Damodaran ERP baseline against today's risk-free real rate of ${currentRealRf.toFixed(2)}%.`}
                            data-testid="valuation-recommend-btn"
                          >
                            ⇢ set {snappedNominal.toFixed(1)}%
                          </button>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })()}
        </div>
      )}

      {result.ok ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="valuation-matrix-result">
          {/* Sep 2026 — DCF blend view toggle. Sits ABOVE the three
              output cells so users can flip between the payout-weighted
              Blend (default), pure DDM (dividend-only), or pure DCF
              (earnings-only). The three cells + explainer below all
              re-render off the selected view. */}
          {dcf.skip !== true && (
            <div className="col-span-full flex items-center justify-between flex-wrap gap-2" data-testid="valuation-view-toggle-row">
              <div
                className="inline-flex text-[10px] font-bold rounded-full border border-border overflow-hidden"
                data-testid="valuation-view-toggle"
              >
                {[
                  ['blend', 'BLEND', 'Payout-weighted blend of DDM (dividend stream) and DCF (full earnings stream). Default — self-adjusts per stock.'],
                  ['ddm', 'DDM', 'Dividend Discount Model — values the dividend stream only. Most accurate for high-payout names (REITs, utilities, preferreds).'],
                  ['dcf', 'DCF', 'Discounted Earnings Model — values the full business using EPS (or FCF/share). Most accurate for low-payout compounders (AAPL, MSFT, V).'],
                ].map(([key, label, title]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setViewMode(key)}
                    title={title}
                    disabled={(key === 'dcf' && !dcf.ok) || (key === 'blend' && !blend.ok)}
                    className={`px-2 py-0.5 transition-colors ${viewMode === key ? 'bg-fuchsia-500 text-white' : 'text-muted-foreground hover:bg-muted/40'} ${(key === 'dcf' && !dcf.ok) || (key === 'blend' && !blend.ok) ? 'opacity-40 cursor-not-allowed' : ''}`}
                    data-testid={`valuation-view-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {(viewMode === 'dcf' || viewMode === 'blend') && dcf.ok && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div
                    className="inline-flex text-[9px] font-bold rounded-full border border-border overflow-hidden"
                    data-testid="valuation-earnings-toggle"
                    title="Which base earnings figure the DCF uses. EPS is GuruFocus's default and historically correlates better with long-run price. FCF/share is the cash-flow alternative — useful when EPS is noisy from write-downs."
                  >
                    <button
                      type="button"
                      onClick={() => setEarningsMode('eps')}
                      className={`px-1.5 py-0.5 transition-colors ${earningsMode === 'eps' ? 'bg-fuchsia-500 text-white' : 'text-muted-foreground hover:bg-muted/40'}`}
                      data-testid="valuation-earnings-eps"
                    >
                      EPS
                    </button>
                    <button
                      type="button"
                      onClick={() => setEarningsMode('fcf')}
                      disabled={!(dcfInputs?.fcf_per_share_ttm > 0)}
                      className={`px-1.5 py-0.5 transition-colors ${earningsMode === 'fcf' ? 'bg-fuchsia-500 text-white' : 'text-muted-foreground hover:bg-muted/40'} ${!(dcfInputs?.fcf_per_share_ttm > 0) ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid="valuation-earnings-fcf"
                    >
                      FCF
                    </button>
                  </div>
                  {dcfInputs?.tangible_book_per_share != null && (
                    <button
                      type="button"
                      onClick={() => setAddTangibleBook(v => !v)}
                      title={`Add tangible book value per share (equity − goodwill − intangibles) to the DCF as a liquidation floor. Current TBV: $${Number(dcfInputs.tangible_book_per_share).toFixed(2)}/share. Useful for asset-heavy names (REITs, banks, industrials) where the balance sheet materially anchors fair value. Negative TBV is added as-is so an over-leveraged balance sheet correctly lowers FV.`}
                      className={`inline-flex items-center px-2 py-0.5 text-[9px] font-bold rounded-full border transition-colors ${addTangibleBook ? 'bg-emerald-500 text-white border-emerald-500' : 'border-border text-muted-foreground hover:bg-muted/40'}`}
                      data-testid="valuation-tbv-toggle"
                    >
                      {addTangibleBook ? '+ Book ✓' : '+ Book'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <div className="text-[10px] font-bold text-foreground uppercase tracking-wide">Current Price</div>
            <div className="font-mono font-bold text-base">${effectiveLive > 0 ? effectiveLive.toFixed(2) : '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-foreground uppercase tracking-wide">
              <AbbrTip term="INTRINSIC_VALUE">Target Buy</AbbrTip>
              {dcf.skip !== true && (
                <span className="ml-1 font-mono text-fuchsia-600 dark:text-fuchsia-400">
                  {viewMode === 'blend'
                    ? (blend.ddmOnly ? '· DDM only' : blend.dcfOnly ? '· DCF only' : '· Blend')
                    : viewMode === 'dcf' ? '· DCF' : '· DDM'}
                </span>
              )}
            </div>
            <div className="font-mono font-bold text-base text-foreground" data-testid="valuation-target-buy">
              {displayValue != null ? `$${displayValue.toFixed(2)}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold text-foreground uppercase tracking-wide">vs Market</div>
            {delta != null ? (
              <div className={`flex items-center gap-1 font-mono font-bold text-base ${
                underValued ? 'text-emerald-600 dark:text-emerald-400'
                : overValued ? 'text-rose-600 dark:text-rose-400'
                : 'text-muted-foreground'
              }`} data-testid="valuation-vs-market">
                {underValued ? <TrendingUp className="w-3.5 h-3.5" /> : overValued ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                {delta >= 0 ? '+' : ''}{(delta * 100).toFixed(1)}%
                <span className="text-[10px] font-bold text-foreground ml-1">
                  {underValued ? <AbbrTip term="UNDERVALUED">Undervalued</AbbrTip>
                    : overValued ? <AbbrTip term="OVERVALUED">Overvalued</AbbrTip>
                    : 'Fair'}
                </span>
              </div>
            ) : (
              <div className="text-sm text-foreground font-bold">No live price</div>
            )}
          </div>
          {result.formula && (
            <div className="col-span-full text-[11px] text-foreground font-mono font-bold mt-1" title="The math used to derive the target buy price.">
              {result.formula}
            </div>
          )}
          {result.assumptions?.length > 0 && (
            <ul className="col-span-full text-[11px] text-foreground font-bold space-y-0.5 mt-1 list-disc list-inside">
              {result.assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          )}

          {/* Sep 2026 — Blend breakdown row. Shows the raw DDM value,
              the raw DCF value, and the payout weight the blend used,
              so users can see where the headline number came from
              regardless of which view mode is active. Hidden for
              preferreds / baby bonds / BDC / CEF where DCF is skipped. */}
          {dcf.skip !== true && (result.ok || dcf.ok) && (
            <div className="col-span-full mt-2 rounded-md border border-dashed border-border/60 bg-muted/20 px-3 py-2 text-[11px] font-mono text-foreground flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="valuation-blend-breakdown">
              <span className="font-bold">
                <AbbrTip term="DDM" className="!font-bold">DDM</AbbrTip>:{' '}
                <span className="text-fuchsia-600 dark:text-fuchsia-400">
                  {result.ok ? `$${result.value.toFixed(2)}` : '—'}
                </span>
              </span>
              <span className="text-muted-foreground/60">·</span>
              <span className="font-bold">
                DCF:{' '}
                <span className="text-fuchsia-600 dark:text-fuchsia-400">
                  {dcf.ok ? `$${dcf.value.toFixed(2)}` : '—'}
                </span>
              </span>
              {blend.ok && !blend.ddmOnly && !blend.dcfOnly && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <span className="font-bold cursor-help" title="Blend weight comes from the trailing dividend-payout ratio. High payout (e.g. REITs at ~90%) tilts the blend toward DDM; low payout (e.g. AAPL at ~15%) tilts it toward DCF.">
                    payout weight: <span className="text-foreground">{(blend.payout * 100).toFixed(0)}% DDM</span>
                    {' / '}
                    <span className="text-foreground">{((1 - blend.payout) * 100).toFixed(0)}% DCF</span>
                    {blend.payoutSource === 'computed' && <span className="text-muted-foreground/80"> (computed from D₀/EPS)</span>}
                    {blend.payoutSource === 'fallback' && <span className="text-amber-600 dark:text-amber-400"> (default — no payout data)</span>}
                  </span>
                </>
              )}
              {blend.ok && blend.ddmOnly && (
                <>
                  <span className="text-muted-foreground/60">·</span>
                  <span className="text-amber-700 dark:text-amber-300 font-bold cursor-help" title="DCF couldn't be computed for this ticker — the blend has reverted to pure DDM (the pre-Sep-2026 behaviour). See the reason on the next line.">
                    reverted to DDM only <span aria-hidden className="opacity-70">ⓘ</span>
                  </span>
                </>
              )}
              {dcf.ok && (
                <span className="w-full text-[10px] text-muted-foreground font-sans">
                  DCF base: {dcf.base === 'fcf' ? 'FCF/share' : 'EPS'} ${Number(dcf.E0).toFixed(2)} ·
                  growth {(dcf.g1Used * 100).toFixed(1)}% (
                  {dcf.isOverridden
                    ? <span className="text-amber-600 dark:text-amber-400">user override, was {Number(dcf.trailingCagrPct ?? 0).toFixed(1)}% {dcf.historyWindow} CAGR</span>
                    : <>{dcf.historyWindow} CAGR
                        {dcf.gClamped === 'high' ? ', capped at 20%' : dcf.gClamped === 'low' ? ', floored at 5%' : ''}</>}
                  ) · terminal {(dcf.g2Used * 100).toFixed(1)}%
                  {!dcf.terminalIsDefault && dcf.terminalSector && (
                    <span className="text-emerald-600 dark:text-emerald-400"> ({dcf.terminalSector} sector)</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowSectorMap(v => !v)}
                    className="ml-1 text-fuchsia-600 dark:text-fuchsia-400 underline decoration-dotted underline-offset-2 hover:text-fuchsia-500"
                    data-testid="valuation-sector-map-toggle"
                    title="Show/hide the full sector → terminal-growth map"
                  >
                    {showSectorMap ? 'Hide Sector Map' : 'Show Sector Map'}
                  </button>
                  · discount {(dcf.dUsed * 100).toFixed(1)}%
                  {dcf.bookAdd !== 0 && (
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {' '}· + tangible book ${Number(dcf.bookAdd).toFixed(2)}/sh
                    </span>
                  )}
                </span>
              )}
              {dcf.ok && (viewMode === 'dcf' || viewMode === 'blend') && (
                <div className="w-full mt-1.5 flex items-center gap-2 flex-wrap" data-testid="valuation-growth-override-row">
                  <label className="text-[10px] font-bold text-foreground uppercase tracking-wide whitespace-nowrap">
                    Growth override
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={25}
                    step={0.5}
                    value={growthOverride != null ? growthOverride * 100 : (dcf.trailingCagrPct ?? dcf.g1Used * 100)}
                    onChange={(e) => setGrowthOverride(Number(e.target.value) / 100)}
                    className="flex-1 min-w-[120px] max-w-[200px] accent-fuchsia-500"
                    data-testid="valuation-growth-override-slider"
                    title="Manually set the DCF growth-stage rate. Analyst forward estimates sometimes deserve to beat the trailing 10-yr CAGR. Values outside 5-20% are clamped by the same GuruFocus rule."
                  />
                  <span className="text-[11px] font-mono font-bold text-foreground" data-testid="valuation-growth-override-value">
                    {growthOverride != null
                      ? `${(growthOverride * 100).toFixed(1)}%`
                      : `${Number(dcf.trailingCagrPct ?? 0).toFixed(1)}% (trailing)`}
                  </span>
                  {growthOverride != null && (
                    <button
                      type="button"
                      onClick={() => setGrowthOverride(null)}
                      className="text-[10px] font-bold text-fuchsia-600 dark:text-fuchsia-400 underline decoration-dotted underline-offset-2 hover:text-fuchsia-500"
                      data-testid="valuation-growth-override-reset"
                    >
                      reset to trailing
                    </button>
                  )}
                </div>
              )}
              {showSectorMap && (
                <div className="w-full mt-2 rounded-md border border-fuchsia-500/40 bg-fuchsia-500/5 px-3 py-2" data-testid="valuation-sector-map">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-400 mb-1">
                    Sector → Terminal Growth (g₂) map
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px] font-mono">
                    {Object.entries(SECTOR_TERMINAL_GROWTH).map(([sec, g2]) => {
                      const active = dcf.terminalSector === sec;
                      return (
                        <div
                          key={sec}
                          className={`flex justify-between ${active ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-foreground'}`}
                          data-testid={`valuation-sector-row-${sec.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <span className="truncate pr-2">{active ? '▸ ' : ''}{sec}</span>
                          <span>{(g2 * 100).toFixed(1)}%</span>
                        </div>
                      );
                    })}
                    <div className={`flex justify-between col-span-full mt-1 pt-1 border-t border-fuchsia-500/20 ${dcf.terminalIsDefault ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-muted-foreground'}`}>
                      <span>{dcf.terminalIsDefault ? '▸ ' : ''}Default (any sector not listed)</span>
                      <span>{(DCF_TERMINAL_GROWTH_DEFAULT * 100).toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="text-[9px] text-muted-foreground mt-1.5 leading-relaxed">
                    None exceeds long-run US nominal GDP (~5%). Utilities and Real Estate are rate-regulated
                    or asset-linked, so their long-run ceiling is at or below inflation. Technology historically
                    compounds fastest and gets the top-of-band 5%.
                  </div>
                </div>
              )}
              {!dcf.ok && dcf.reason && !dcf.skip && (
                <span className="w-full text-[10px] text-amber-700 dark:text-amber-300 font-sans">
                  DCF unavailable — {dcf.reason}
                </span>
              )}
            </div>
          )}

          {/* Round 95c v3 (Jun 2026) — Explainer block. Sep 2026 — updated
              to describe both DDM and DCF sides and the payout-weighted
              blend that combines them. */}
          <div className="col-span-full mt-3 rounded-md border bg-muted/30 p-3 space-y-2 text-[12px] font-bold leading-relaxed text-foreground" data-testid="valuation-matrix-explainer">
            <p>
              <span className="text-fuchsia-600 dark:text-fuchsia-400">What this model does · </span>
              Two intrinsic-value engines run side by side, then blend by <AbbrTip term="PAYOUT_RATIO">payout ratio</AbbrTip>. The <AbbrTip term="DDM">Dividend Discount Model</AbbrTip> values just the dividend stream you receive ({'\u2264'}6% CAGR uses <AbbrTip term="GORDON_DDM">Gordon Constant Growth</AbbrTip>, faster growers use the <AbbrTip term="TWO_STAGE_DDM">Two-Stage DDM</AbbrTip>; preferreds &amp; baby bonds use the <AbbrTip term="ZERO_GROWTH_PERPETUITY">Zero-Growth Perpetuity</AbbrTip>). The <AbbrTip term="DCF_MODEL">DCF</AbbrTip> (Discounted Earnings) values the full business — a GuruFocus-style two-stage model on EPS (or FCF/share) with a 10-year growth stage at the trailing 10-yr CAGR (capped 5–20%) followed by a 10-year terminal stage at a <strong>sector-aware</strong> rate (2% utilities, 3% real estate/energy, 4% default, 5% technology). The <strong>Blend</strong> weights each by payout ratio: high-payout names (REITs, utilities) tilt toward DDM; low-payout compounders (AAPL, MSFT, V) tilt toward DCF, which is why the headline number for a mega-cap is now realistic instead of the dividend-only figure.
            </p>
            <p>
              <span className="text-fuchsia-600 dark:text-fuchsia-400">Why the slider matters · </span>
              The <AbbrTip term="REQUIRED_RETURN">required rate of return</AbbrTip> is YOUR personal hurdle — how much annual return you need before this stock is worth the risk. Slide RIGHT (12–15%, aggressive) and BOTH engines demand a CHEAPER entry price. Slide LEFT (6–7%, conservative) and both are willing to pay more. If your r is lower than the company&apos;s dividend growth or earnings growth rate, the math breaks; we automatically clamp growth DOWN and flag it in the assumptions above so the result is always conservative and never zero.
            </p>
            <p>
              <span className="text-fuchsia-600 dark:text-fuchsia-400">Toggle the view · </span>
              <strong>BLEND</strong> is the default and self-adjusts per stock — usually the most honest single number. Switch to <strong>DDM</strong> to see the &quot;what the dividend alone justifies&quot; number (useful for dividend-first investors). Switch to <strong>DCF</strong> to see the &quot;what the full business is worth&quot; number (useful for growth investors and buyback-heavy mega-caps). If EPS is negative we auto-fall-back to FCF/share for the DCF base; you can also flip that manually with the EPS/FCF pill.
            </p>
            <p>
              <span className="text-fuchsia-600 dark:text-fuchsia-400">Tangible Book add-on · </span>
              The <strong>+ Book</strong> pill adds tangible book value per share (equity − goodwill − intangibles) to the DCF as a <strong>liquidation floor</strong>. This gives asset-heavy names credit for what the balance sheet is worth if the business winds down — most useful for REITs, banks, insurers, and industrials, where the physical/financial assets materially anchor fair value. It&apos;s auto-suggested ON for those sectors and OFF elsewhere, and negative tangible book is added as-is (so an over-leveraged balance sheet correctly LOWERS fair value).
            </p>
            <p>
              <span className="text-fuchsia-600 dark:text-fuchsia-400">Growth override · </span>
              The trailing 10-yr CAGR is a great starting point, but it&apos;s backward-looking. When you have a strong view — analyst forward estimates, a known product cycle, a margin inflection — drag the <strong>Growth override</strong> slider (0–25%) to set the DCF growth stage manually. The 5–20% clamp still applies to keep the model sane. Values above the trailing rate reflect a bullish forward view; below reflect a bearish one. Click <em>reset to trailing</em> to go back to the data-driven number. The <strong>Show Sector Map</strong> link exposes the full sector → g₂ table so you can see which terminal rate the model is applying to any sector at a glance.
            </p>
            {overValued && delta != null && delta < -0.20 && (
              <p data-testid="valuation-matrix-overvalued-explainer">
                <span className="text-rose-600 dark:text-rose-400">Why ${(effectiveLive || 0).toFixed(2)} is &quot;<AbbrTip term="OVERVALUED">Overvalued</AbbrTip> by {Math.abs(delta * 100).toFixed(0)}%&quot; · </span>
                Even the blended model — which already gives full credit for retained earnings, buybacks, and future growth — says the market is asking more than the underlying cash-flow stream justifies at your required return. That&apos;s not automatically a &quot;sell&quot;: some of the gap is the growth story the market is willing to pay a premium for. Pair this with price-based tools like <AbbrTip term="PEG">PEG</AbbrTip>, the Triangle Score, payout-ratio safety, and your own conviction before acting.
              </p>
            )}
            {underValued && delta != null && delta > 0.20 && (
              <p data-testid="valuation-matrix-undervalued-explainer">
                <span className="text-emerald-600 dark:text-emerald-400">Why ${(effectiveLive || 0).toFixed(2)} is &quot;<AbbrTip term="UNDERVALUED">Undervalued</AbbrTip> by {(delta * 100).toFixed(0)}%&quot; · </span>
                The combined earnings + dividend stream — given your required rate of return — already justifies more than the market is charging. Caveat: a struggling REIT or BDC can show undervalued right up until the dividend cut, and a value-trap common stock can show undervalued right up until margins compress. Use this as a sanity check, not a green light: cross-reference the Triangle Score and payout-ratio safety before adding.
              </p>
            )}
          </div>

          {assetClass === 'adr' && (
            <div className="col-span-full flex items-start gap-1.5 text-[11px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/40 rounded px-2 py-1 mt-1" data-testid="valuation-adr-note">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                <AbbrTip term="ADR_TAX_DRAG">ADRs</AbbrTip> convert local-currency dividends to USD after foreign withholding tax — the historical CAGR can look jagged. Treat this estimate as a rough fair-value sketch, not a pinpoint target.
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-start gap-2 text-xs text-foreground font-bold bg-muted/40 rounded px-3 py-2" data-testid="valuation-matrix-skip">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            {result.model && <div className="font-bold text-foreground mb-0.5">{result.model}</div>}
            <div>{result.reason}</div>
            {/* Sep 2026 — BDC-specific replacement for the old "NAV
                gauge coming soon" text. Renders NOTHING if neither
                Price/NAV nor NII coverage data is available, so we
                never regress on tickers without full BDC data. */}
            {assetClass === 'bdc' && (
              <BdcNavGauge stock={stock} livePrice={livePrice} />
            )}
            {/* Sep 2026 — CEF NII coverage carve-out. Some CEFs (credit
                funds, CEF-of-BDCs) DO have meaningful SEC-reported
                distribution coverage. Show the strip when available;
                render nothing otherwise (Z-score gauge above already
                handles the discount-to-NAV angle). */}
            {assetClass === 'cef' && stock?.sec_coverage?.status === 'ok' && (
              <div className="mt-2 pt-2 border-t border-amber-300/40 dark:border-amber-700/30">
                <NiiCoverageStrip
                  coverage={stock.sec_coverage}
                  label="SEC Distribution Coverage"
                  testIdPrefix="cef-coverage"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
